import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, authClient, formatApiError } from "@/lib/auth";
import { toast } from "sonner";
import SectionHeader from "@/components/SectionHeader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { sfx } from "@/lib/sounds";
import { Upload, X, ImageIcon, Users, Server, Loader2 } from "lucide-react";

const CATEGORIES = [
  "Spawn",
  "Medieval",
  "Mega Base",
  "Arena",
  "Cyber",
  "Village",
  "Redstone",
  "Memory",
  "Other",
];

async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function downscale(dataUrl, maxWidth = 1400) {
  return new Promise((resolve) => {
    const img = new Image();

    img.onload = () => {
      let { width, height } = img;

      if (width <= maxWidth) {
        return resolve(dataUrl);
      }

      const ratio = maxWidth / width;

      const canvas = document.createElement("canvas");
      canvas.width = maxWidth;
      canvas.height = Math.round(height * ratio);

      const ctx = canvas.getContext("2d");

      if (!ctx) {
        return resolve(dataUrl);
      }

      ctx.drawImage(
        img,
        0,
        0,
        canvas.width,
        canvas.height
      );

      resolve(canvas.toDataURL("image/jpeg", 0.82));
    };

    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

export default function BuildUpload() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "Spawn",
    coordinates: "",
    tags: "",
    server: "",
  });

  const [servers, setServers] = useState([]);
  const [serversLoading, setServersLoading] = useState(true);

  const [team, setTeam] = useState(null);
  const [teamLoading, setTeamLoading] = useState(false);

  const [images, setImages] = useState([]);
  const [busy, setBusy] = useState(false);

  // Upload progress percentage
  const [uploadProgress, setUploadProgress] = useState(0);


  useEffect(() => {
    if (loading || !user) return;

    let cancelled = false;

    const loadServers = async () => {
      setServersLoading(true);

      try {
        const res = await authClient.get("/servers");

        const data = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.servers)
            ? res.data.servers
            : [];

        const enabled = data.filter(
          (server) => server?.enabled !== false
        );

        if (cancelled) return;

        setServers(enabled);

        if (!form.server && enabled.length > 0) {
          const survival =
            enabled.find((s) => s.key === "survival-s9");

          setForm((prev) => ({
            ...prev,
            server: survival?.key || enabled[0].key,
          }));
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(
            formatApiError(err) || "Failed to load servers"
          );
        }
      } finally {
        if (!cancelled) {
          setServersLoading(false);
        }
      }
    };

    loadServers();

    return () => {
      cancelled = true;
    };
  }, [loading, user]);

  useEffect(() => {
    if (!form.server || serversLoading || !user) {
      setTeam(null);
      return;
    }

    let cancelled = false;

    const loadTeam = async () => {
      setTeamLoading(true);
      setTeam(null);

      try {
        const res = await authClient.get(
          `/builds/team/${encodeURIComponent(form.server)}`
        );

        if (!cancelled) {
          setTeam(res.data?.team || null);
        }
      } catch (err) {
        if (!cancelled) {
          setTeam(null);

          const message =
            err?.response?.data?.detail ||
            err?.message ||
            "Unable to detect your team";

          toast.error(message);
        }
      } finally {
        if (!cancelled) {
          setTeamLoading(false);
        }
      }
    };

    loadTeam();

    return () => {
      cancelled = true;
    };
  }, [form.server, serversLoading, user]);

  if (!loading && !user) {
    navigate("/");
    return null;
  }

  const addFiles = async (files) => {
    const remaining = 6 - images.length;

    if (remaining <= 0) return;

    const arr = Array.from(files).slice(0, remaining);

    for (const f of arr) {
      if (!f.type.startsWith("image/")) continue;

      const raw = await fileToDataUrl(f);
      const smaller = await downscale(raw, 1400);

      setImages((prev) => {
        if (prev.length >= 6) return prev;
        return [...prev, smaller];
      });
    }
  };

  const submit = async (e) => {
    e.preventDefault();

    if (!form.title.trim()) {
      return toast.error("Title required");
    }

    if (!form.server) {
      return toast.error("Select a server");
    }

    if (teamLoading) {
      return toast.error("Still detecting your team");
    }

    if (images.length === 0) {
      return toast.error("Add at least one screenshot");
    }

    setBusy(true);

    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category,
        coordinates: form.coordinates.trim(),
        tags: form.tags
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        server: form.server,
        images,
      };

      // Reset progress before starting the upload.
      setUploadProgress(0);

      await authClient.post("/builds", payload, {
        onUploadProgress: (event) => {
          if (!event.total) return;

          const percent = Math.round(
            (event.loaded / event.total) * 100
          );

          setUploadProgress(Math.min(100, percent));
        },
      });

      setUploadProgress(100);

      sfx.levelup();

      toast.success("Build submitted!", {
        description: team
          ? `Submitted under ${team.name}. Pending admin review.`
          : "Submitted as an individual build. Pending admin review.",
      });

      navigate("/profile");
    } catch (err) {
      sfx.error();
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="pt-32 pb-24 px-4 sm:px-6"
      data-testid="page-build-upload"
    >
      <div className="max-w-3xl mx-auto">

        {busy && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-md px-4">
            <div className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0b0b0f]/95 shadow-2xl p-7">

              {/* Animated upload icon */}
              <div className="flex justify-center mb-6">
                <div className="relative flex items-center justify-center w-20 h-20 rounded-full bg-[#FFBF00]/10 border border-[#FFBF00]/20">

                  <div className="absolute inset-0 rounded-full border border-[#FFBF00]/20 animate-ping" />

                  <div className="relative flex items-center justify-center w-14 h-14 rounded-full bg-[#FFBF00]/15">
                    <Upload
                      size={25}
                      className="text-[#FFBF00] animate-bounce"
                    />
                  </div>

                </div>
              </div>

              {/* Title */}
              <div className="text-center">
                <h3 className="text-xl font-bold text-white">
                  Uploading Your Build
                </h3>

                <p className="text-sm text-white/50 mt-2">
                  Please don't close this page while your build is being uploaded.
                </p>
              </div>

              {/* Progress percentage */}
              <div className="flex items-end justify-between mt-7 mb-2">
                <span className="text-xs uppercase tracking-widest text-white/40">
                  Upload Progress
                </span>

                <span className="text-2xl font-bold text-[#FFBF00]">
                  {uploadProgress}%
                </span>
              </div>

              {/* Progress bar */}
              <div className="h-3 rounded-full bg-white/5 border border-white/10 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[#FFBF00] via-[#FFD95A] to-[#FF8C00] transition-all duration-300 ease-out relative overflow-hidden"
                  style={{
                    width: `${uploadProgress}%`,
                  }}
                >
                  <div className="absolute inset-0 bg-white/20 animate-pulse" />
                </div>
              </div>

              {/* Upload status */}
              <div className="flex items-center justify-center gap-2 mt-5 text-sm text-white/60">
                <Loader2
                  size={15}
                  className="animate-spin text-[#FFBF00]"
                />

                {uploadProgress >= 100
                  ? "Processing your build..."
                  : uploadProgress > 0
                    ? `Uploading ${images.length} image${images.length === 1 ? "" : "s"}...`
                    : "Preparing your build..."}
              </div>

            </div>
          </div>
        )}



        <SectionHeader
          eyebrow="Community Builds"
          title="Upload Your Build"
          subtitle="Showcase your creation with screenshots, coordinates and your verified team."
        />

        <form
          onSubmit={submit}
          className="rounded-2xl glass p-6 space-y-5"
        >

          {/* TITLE */}
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">
              Title *
            </span>

            <Input
              data-testid="build-title"
              value={form.title}
              onChange={(e) =>
                setForm({
                  ...form,
                  title: e.target.value,
                })
              }
              className="bg-black/60 border-white/10 text-white"
              placeholder="My Epic Minecraft Build"
            />
          </label>

          {/* SERVER + CATEGORY */}
          <div className="grid sm:grid-cols-2 gap-4">

            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">
                Server *
              </span>

              <div className="relative">
                <Server
                  size={15}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none"
                />

                <select
                  data-testid="build-server"
                  value={form.server}
                  disabled={serversLoading || busy}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      server: e.target.value,
                    })
                  }
                  className="w-full bg-black/60 border border-white/10 rounded-md pl-9 pr-3 py-2 text-white outline-none focus:border-[#FFBF00]/60 disabled:opacity-60"
                >
                  <option value="">
                    {serversLoading
                      ? "Loading servers..."
                      : "Select server"}
                  </option>

                  {servers.map((server) => (
                    <option
                      key={server.key}
                      value={server.key}
                    >
                      {server.name || server.key}
                    </option>
                  ))}
                </select>
              </div>
            </label>

            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">
                Category
              </span>

              <select
                data-testid="build-category"
                value={form.category}
                onChange={(e) =>
                  setForm({
                    ...form,
                    category: e.target.value,
                  })
                }
                className="w-full bg-black/60 border border-white/10 rounded-md px-3 py-2 text-white"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>

          </div>

          {/* AUTOMATIC TEAM */}
          <div className="rounded-xl border border-white/10 bg-black/40 p-4">

            <div className="flex items-center justify-between gap-3">

              <div className="flex items-center gap-3">

                <div className="w-10 h-10 rounded-xl bg-[#FFBF00]/10 border border-[#FFBF00]/20 grid place-items-center">
                  <Users
                    size={18}
                    className="text-[#FFBF00]"
                  />
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40">
                    Your Team
                  </div>

                  {teamLoading ? (
                    <div className="flex items-center gap-2 text-sm text-white/60 mt-1">
                      <Loader2
                        size={14}
                        className="animate-spin"
                      />
                      Detecting team...
                    </div>
                  ) : team ? (
                    <div className="text-white font-semibold mt-1">
                      {team.name}
                    </div>
                  ) : (
                    <div className="text-white font-semibold mt-1">
                      Individual
                    </div>
                  )}
                </div>

              </div>

              {team ? (
                <div className="text-[9px] uppercase tracking-widest px-2 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-300">
                  Team Verified
                </div>
              ) : !teamLoading && form.server ? (
                <div className="text-[9px] uppercase tracking-widest px-2 py-1 rounded-full bg-white/5 border border-white/10 text-white/50">
                  Individual
                </div>
              ) : null}

            </div>

            {!teamLoading && !team && form.server && (
              <p className="text-xs text-white/40 mt-3">
                No team detected. This build will be uploaded as an individual build.
              </p>
            )}

          </div>

          {/* DESCRIPTION */}
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">
              Description
            </span>

            <Textarea
              data-testid="build-description"
              rows={4}
              value={form.description}
              onChange={(e) =>
                setForm({
                  ...form,
                  description: e.target.value,
                })
              }
              className="bg-black/60 border-white/10 text-white"
              placeholder="Tell the community about your build..."
            />
          </label>

          {/* COORDINATES */}
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">
              Coordinates
            </span>

            <Input
              placeholder="e.g. -123, 65, 890"
              value={form.coordinates}
              onChange={(e) =>
                setForm({
                  ...form,
                  coordinates: e.target.value,
                })
              }
              className="bg-black/60 border-white/10 text-white"
            />
          </label>

          {/* TAGS */}
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">
              Tags
            </span>

            <Input
              placeholder="epic, redstone, medieval"
              value={form.tags}
              onChange={(e) =>
                setForm({
                  ...form,
                  tags: e.target.value,
                })
              }
              className="bg-black/60 border-white/10 text-white"
            />
          </label>

          {/* SCREENSHOTS */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2">
              Screenshots (up to 6)
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">

              {images.map((src, i) => (
                <div
                  key={i}
                  className="relative aspect-video rounded-lg overflow-hidden border border-white/10"
                >
                  <img
                    src={src}
                    alt=""
                    className="w-full h-full object-cover"
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setImages(
                        images.filter(
                          (_, idx) => idx !== i
                        )
                      )
                    }
                    className="absolute top-1 right-1 p-1 rounded-full bg-black/70 hover:bg-red-500/70"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}

              {images.length < 6 && (
                <button
                  type="button"
                  data-testid="build-add-image"
                  onClick={() =>
                    inputRef.current?.click()
                  }
                  className="aspect-video rounded-lg border-2 border-dashed border-white/15 hover:border-[#FFBF00]/50 grid place-items-center text-white/50 hover:text-[#FFBF00]"
                >
                  <div className="flex flex-col items-center gap-1">
                    <ImageIcon size={20} />
                    <span className="text-[10px] uppercase tracking-widest">
                      Add
                    </span>
                  </div>
                </button>
              )}

            </div>

            <input
              ref={inputRef}
              type="file"
              multiple
              accept="image/*"
              className="hidden"
              onChange={(e) =>
                addFiles(e.target.files || [])
              }
            />
          </div>

          {/* SUBMIT */}
          <button
            type="submit"
            disabled={
              busy ||
              serversLoading ||
              teamLoading ||
              !form.server
            }
            data-testid="build-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#FFBF00] text-black font-semibold uppercase tracking-wider hover:bg-[#FF8C00] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Upload size={16} />

            {busy
              ? "Uploading..."
              : teamLoading
                ? "Detecting Team..."
                : "Submit for Approval"}
          </button>

        </form>
      </div>
    </div>
  );
}
